/**
 * Routing and the application shell.
 *
 * Unauthenticated visitors see the login form and nothing else
 * (requirement 13.2) — not a redirect from a rendered layout, which would leak
 * the navigation structure.
 */

import { useState, type ReactNode } from "react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";

import { ErrorNotice } from "./components";
import { useAuth } from "./auth";
import {
  ActivityIcon,
  JobsIcon,
  LibraryIcon,
  ProjectsIcon,
  SettingsIcon,
  SignOutIcon,
  UsersIcon,
} from "./icons";
import { UsersPage } from "./accounts";
import {
  ActivityPage,
  BlockDefinitionPage,
  LibraryPage,
  DesignPage,
  JobsPage,
  ProjectPage,
  ProjectsPage,
  SettingsPage,
} from "./pages";

export default function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return <p className="muted" style={{ padding: "2rem" }}>Loading…</p>;
  }

  if (!user) {
    return <LoginPage />;
  }

  return <Shell />;
}

/**
 * An icon rail rather than a labelled sidebar, so the editor gets the width.
 * Each icon names itself in a tooltip on hover or keyboard focus, and to
 * assistive technology through its accessible name.
 */
function RailLink({ to, label, children }: { to: string; label: string; children: ReactNode }) {
  return (
    <NavLink to={to} className="rail-item" aria-label={label}>
      {children}
      <span className="rail-tip" aria-hidden="true">
        {label}
      </span>
    </NavLink>
  );
}

function Shell() {
  const { user } = useAuth();
  // A design fills the page edge to edge: the workspace is the page.
  const path = useLocation().pathname;
  const fullBleed = path.startsWith("/designs/") || path.startsWith("/library/blocks/");
  return (
    <div className="shell">
      <nav className="rail" aria-label="Main">
        <img className="rail-mark" src="/fluidea-icon.svg" alt="" aria-hidden="true" />
        <RailLink to="/projects" label="Projects">
          <ProjectsIcon />
        </RailLink>
        <RailLink to="/library" label="Library">
          <LibraryIcon />
        </RailLink>
        <RailLink to="/activity" label="Activity">
          <ActivityIcon />
        </RailLink>
        <RailLink to="/jobs" label="Jobs">
          <JobsIcon />
        </RailLink>
        <span className="rail-spacer" />
        {/* Shown to administrators only; the server refuses everyone else (D37). */}
        {user?.is_admin && (
          <RailLink to="/users" label="Users">
            <UsersIcon />
          </RailLink>
        )}
        <RailLink to="/settings" label="Settings">
          <SettingsIcon />
        </RailLink>
        <UserPanel />
      </nav>
      <main className={fullBleed ? "content content-full" : "content"}>
        <Routes>
          <Route path="/" element={<Navigate to="/projects" replace />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/:projectId" element={<ProjectPage />} />
          <Route path="/designs/:designId" element={<DesignPage />} />
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/library/blocks/:entityId" element={<BlockDefinitionPage />} />
          {/* Programmes are sections of the Projects page now. */}
          <Route path="/programs" element={<Navigate to="/projects" replace />} />
          <Route path="/activity" element={<ActivityPage />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="*" element={<p>Not found.</p>} />
        </Routes>
      </main>
    </div>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? parts[parts.length - 1]![0] : "")).toUpperCase() || "?";
}

/** Who is signed in, as initials with the full name in the tooltip, and a way out. */
function UserPanel() {
  const { user, logOut } = useAuth();
  if (!user) return null;

  return (
    <>
      <div className="rail-item rail-user" tabIndex={0} aria-label={`Signed in as ${user.display_name}, ${user.email}`}>
        <span className="rail-avatar" aria-hidden="true">
          {initials(user.display_name)}
        </span>
        <span className="rail-tip" aria-hidden="true">
          {user.display_name}
          <span className="rail-tip-sub">{user.email}</span>
        </span>
      </div>
      <button type="button" className="rail-item" aria-label="Sign out" onClick={() => void logOut()}>
        <SignOutIcon />
        <span className="rail-tip" aria-hidden="true">
          Sign out
        </span>
      </button>
    </>
  );
}

function LoginPage() {
  const { logIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await logIn(email, password);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login">
      <h1 className="login-title">
        <img src="/fluidea-icon.svg" alt="" aria-hidden="true" width={40} height={40} />
        Fluidea
      </h1>
      <form className="card" onSubmit={submit}>
        <label>
          Email
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="username"
            required
            style={{ width: "100%" }}
          />
        </label>
        <label>
          Password
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
            style={{ width: "100%" }}
          />
        </label>
        <ErrorNotice error={error} />
        <button className="primary" type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
      <p className="muted">
        Accounts are provisioned by an administrator. There is no self-registration.
      </p>
    </div>
  );
}
